import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

import { translations } from "@app/i18n";

/**
 * A single editable invoice line item row.
 *
 * `invoiceItemID` is non-null only for rows that came back from the server;
 * rows the user just added carry `null` and exist only in memory.
 */
export interface InvoiceLineItem {
  id: string;
  itemName: string;
  itemAmount: number | null;
  invoiceItemID: number | null;
}

/** Label written into the invoice's fee heading when the user doesn't change it. */
export const DEFAULT_INVOICE_LABEL = "FEES";

/** Emitted in `server` delete mode so the host can run the API call itself. */
export interface InvoiceLineItemServerDeleteEvent {
  id: string;
  invoiceItemID: number;
}

/** Local row identity. Exported so hosts mapping server items reuse the same scheme. */
export function generateLineItemId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/** Builds a blank row. Exported so hosts seed `items` without copying the id scheme. */
export function createEmptyInvoiceLineItem(): InvoiceLineItem {
  return {
    id: generateLineItemId(),
    itemName: "",
    itemAmount: null,
    invoiceItemID: null,
  };
}

/** True when every row has a non-blank name and an amount greater than zero. */
export function areInvoiceLineItemsValid(items: InvoiceLineItem[]): boolean {
  return items.every(
    (item) =>
      item.itemName.trim().length > 0 &&
      item.itemAmount !== null &&
      item.itemAmount > 0,
  );
}

/** The table always shows at least one row, so an emptied list falls back to a blank one. */
export function ensureAtLeastOneLineItem(
  items: InvoiceLineItem[],
): InvoiceLineItem[] {
  return items.length === 0 ? [createEmptyInvoiceLineItem()] : items;
}

@Component({
  selector: "app-invoice-line-items",
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./invoice-line-items.component.html",
  styleUrl: "./invoice-line-items.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceLineItemsComponent {
  /** Bind with `[(items)]`. The host stays the source of truth. Never emits an empty array. */
  readonly items = model.required<InvoiceLineItem[]>();

  /** Renders the free-text label input above the table. Off by default. */
  readonly showLabelField = input<boolean>(false);
  readonly label = model<string>(DEFAULT_INVOICE_LABEL);
  readonly labelFieldLabel = input<string>(
    translations.invoiceLineItems.labelField.label,
  );
  readonly labelFieldPlaceholder = input<string>(
    translations.invoiceLineItems.labelField.placeholder,
  );

  /** Makes every input readonly and disables Add / Delete. */
  readonly isReadonly = input<boolean>(false);

  /**
   * `local` — every delete is an in-memory splice. The create dialog needs this so
   * Cancel can discard everything; nothing has been persisted yet.
   *
   * `server` — rows carrying an `invoiceItemID` emit `serverDelete` instead of being
   * spliced, leaving the host to call the API and write the shortened array back
   * through `items`. Rows without an id are still spliced locally.
   */
  readonly deleteMode = input<"local" | "server">("local");

  /** Host-controlled spinner for the row currently being deleted server-side. */
  readonly deletingItemId = input<string | null>(null);
  readonly serverDelete = output<InvoiceLineItemServerDeleteEvent>();

  readonly currencySymbol = input<string>("$");

  /**
   * Classes for the table wrapper. The default matches the task page. Dialogs pass
   * `fs-table` alone, because the global `.table-form` rule pins input width to 280px
   * (see `styles/material/_mdc-input.scss`), which breaks a fluid dialog layout.
   */
  readonly tableWrapperClass = input<string>("fs-table table-form");

  readonly allRowsValid = computed(() => areInvoiceLineItemsValid(this.items()));

  readonly translations = translations;

  addLineItem(): void {
    this.items.update((items) => [...items, createEmptyInvoiceLineItem()]);
  }

  removeLineItem(id: string): void {
    const itemToRemove = this.items().find((item) => item.id === id);

    if (!itemToRemove) {
      return;
    }

    if (this.deleteMode() === "server" && itemToRemove.invoiceItemID) {
      this.serverDelete.emit({ id, invoiceItemID: itemToRemove.invoiceItemID });
      return;
    }

    this.items.update((items) =>
      ensureAtLeastOneLineItem(items.filter((item) => item.id !== id)),
    );
  }

  updateItemName(id: string, value: string): void {
    this.items.update((items) =>
      items.map((item) =>
        item.id === id ? { ...item, itemName: value } : item,
      ),
    );
  }

  updateItemAmount(id: string, value: string): void {
    const numValue = value === "" ? null : Number.parseFloat(value);
    this.items.update((items) =>
      items.map((item) =>
        item.id === id ? { ...item, itemAmount: numValue } : item,
      ),
    );
  }

  updateLabel(value: string): void {
    this.label.set(value);
  }

  canDeleteRow(id: string): boolean {
    const item = this.items().find((i) => i.id === id);
    if (!item) return false;
    return (
      item.itemName.trim().length > 0 ||
      item.itemAmount !== null ||
      !!item.invoiceItemID
    );
  }
}
